import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Star, Target, Briefcase, ArrowLeft, ArrowRight, Check, HelpCircle, Info, MapPin, Lightbulb, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate a simple unique ID
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

const categoryIcons = {
  passion: Heart,
  talent: Star,
  mission: Target,
  profession: Briefcase
};

const categoryColors = {
  passion: '#FB6823',
  talent: '#A4187A',
  mission: '#00C853',
  profession: '#2196F3'
};

// Instruction and guidance messages
const messages = {
  fr: {
    instruction: "Choisis la réponse qui te correspond le mieux.",
    guidance: "Il n'y a pas de bonne ou de mauvaise réponse. Fais confiance à ton instinct !",
    subGuidance: "Même si plusieurs réponses pourraient te correspondre, choisis celle qui te parle le plus spontanément.",
    neutralOption: "Je ne sais pas encore",
    neutralOptionAlt: "Aucune de ces réponses ne me correspond vraiment",
    preTestTitle: "Avant de commencer",
    preTestSubtitle: "Quelques questions pour mieux te connaître",
    municipalityLabel: "Dans quelle commune ou ville étudies-tu ou habites-tu ?",
    municipalityPlaceholder: "Ex: Bruxelles, Liège, Namur...",
    municipalityOptional: "(Optionnel)",
    startTest: "Commencer le test Ikigai",
    demographicNotice: "Ces informations nous aident à mieux comprendre les profils des jeunes et à améliorer l'outil Déclic.",
    genderLabel: "Genre",
    ageGroupLabel: "Tranche d'âge",
    skipSection: "Passer cette étape",
    trustInstinct: "Fais confiance à ton instinct",
    noPerfectAnswer: "Il n'y a pas de réponse parfaite",
    stage1Duration: "Durée estimée : 5-7 minutes",
    questionOf: "Question {current} sur {total}",
  },
  nl: {
    instruction: "Kies het antwoord dat het beste bij je past.",
    guidance: "Er is geen goed of fout antwoord. Vertrouw op je instinct!",
    subGuidance: "Ook als meerdere antwoorden zouden kunnen passen, kies het antwoord dat je het meest spontaan aanspreekt.",
    neutralOption: "Ik weet het nog niet",
    neutralOptionAlt: "Geen van deze antwoorden past echt bij mij",
    preTestTitle: "Voor we beginnen",
    preTestSubtitle: "Enkele vragen om je beter te leren kennen",
    municipalityLabel: "In welke gemeente of stad studeer of woon je?",
    municipalityPlaceholder: "Bv: Brussel, Antwerpen, Gent...",
    municipalityOptional: "(Optioneel)",
    startTest: "Start de Ikigai test",
    demographicNotice: "Deze informatie helpt ons de profielen van jongeren beter te begrijpen en de Déclic tool te verbeteren.",
    genderLabel: "Geslacht",
    ageGroupLabel: "Leeftijdsgroep",
    skipSection: "Deze stap overslaan",
    trustInstinct: "Vertrouw op je instinct",
    noPerfectAnswer: "Er is geen perfect antwoord",
    stage1Duration: "Geschatte duur: 5-7 minuten",
    questionOf: "Vraag {current} van {total}",
  }
};

// Demographic options
const demographicOptions = {
  fr: {
    gender: [
      { value: "", text: "-- Choisir (optionnel) --" },
      { value: "male", text: "Homme" },
      { value: "female", text: "Femme" },
      { value: "other", text: "Autre" },
      { value: "prefer_not", text: "Je préfère ne pas répondre" }
    ],
    genderRequired: [
      { value: "", text: "-- Choisir --" },
      { value: "male", text: "Homme" },
      { value: "female", text: "Femme" },
      { value: "other", text: "Autre" },
      { value: "prefer_not", text: "Je préfère ne pas répondre" }
    ],
    ageGroup: [
      { value: "", text: "-- Choisir (optionnel) --" },
      { value: "13-15", text: "13-15 ans" },
      { value: "16-18", text: "16-18 ans" },
      { value: "19-22", text: "19-22 ans" },
      { value: "23+", text: "23 ans et +" }
    ],
    ageGroupRequired: [
      { value: "", text: "-- Choisir --" },
      { value: "13-15", text: "13-15 ans" },
      { value: "16-18", text: "16-18 ans" },
      { value: "19-22", text: "19-22 ans" },
      { value: "23+", text: "23 ans et +" }
    ]
  },
  nl: {
    gender: [
      { value: "", text: "-- Kies (optioneel) --" },
      { value: "male", text: "Man" },
      { value: "female", text: "Vrouw" },
      { value: "other", text: "Andere" },
      { value: "prefer_not", text: "Ik geef dit liever niet op" }
    ],
    genderRequired: [
      { value: "", text: "-- Kies --" },
      { value: "male", text: "Man" },
      { value: "female", text: "Vrouw" },
      { value: "other", text: "Andere" },
      { value: "prefer_not", text: "Ik geef dit liever niet op" }
    ],
    ageGroup: [
      { value: "", text: "-- Kies (optioneel) --" },
      { value: "13-15", text: "13-15 jaar" },
      { value: "16-18", text: "16-18 jaar" },
      { value: "19-22", text: "19-22 jaar" },
      { value: "23+", text: "23 jaar en ouder" }
    ],
    ageGroupRequired: [
      { value: "", text: "-- Kies --" },
      { value: "13-15", text: "13-15 jaar" },
      { value: "16-18", text: "16-18 jaar" },
      { value: "19-22", text: "19-22 jaar" },
      { value: "23+", text: "23 jaar en ouder" }
    ]
  }
};

// Pre-test impact questions
const preTestQuestions = {
  fr: {
    question: "Aujourd'hui, dirais-tu que tu sais déjà quel métier t'intéresse ?",
    answers: [
      { value: "clear_idea", text: "J'ai déjà une idée claire", icon: "💡" },
      { value: "some_ideas", text: "J'ai quelques idées mais je ne suis pas sûr(e)", icon: "🤔" },
      { value: "no_idea", text: "Je n'ai pas encore d'idée", icon: "🔍" }
    ]
  },
  nl: {
    question: "Zou je vandaag zeggen dat je al weet welk beroep je interesseert?",
    answers: [
      { value: "clear_idea", text: "Ik heb al een duidelijk idee", icon: "💡" },
      { value: "some_ideas", text: "Ik heb enkele ideeën maar ik ben niet zeker", icon: "🤔" },
      { value: "no_idea", text: "Ik heb nog geen idee", icon: "🔍" }
    ]
  }
};

export default function Questionnaire() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [questions, setQuestions] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Pre-test state
  const [showPreTest, setShowPreTest] = useState(true);
  const [preTestAnswer, setPreTestAnswer] = useState(null);
  const [municipality, setMunicipality] = useState('');
  const [municipalities, setMunicipalities] = useState([]);
  const [showMunicipalitySuggestions, setShowMunicipalitySuggestions] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  
  // Demographic state (optional)
  const [gender, setGender] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  
  // Auto-detected geolocation
  const [detectedLocation, setDetectedLocation] = useState(null);

  const msg = messages[language] || messages.fr;
  const preTest = preTestQuestions[language] || preTestQuestions.fr;
  const demoOptions = demographicOptions[language] || demographicOptions.fr;

  useEffect(() => {
    fetchQuestions();
    fetchMunicipalities();
    detectLocation();
  }, [language]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions/${language}`);
      setQuestions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setLoading(false);
    }
  };

  const fetchMunicipalities = async () => {
    try {
      const response = await axios.get(`${API}/analytics/municipalities`);
      setMunicipalities(response.data.municipalities || []);
    } catch (error) {
      console.error('Error fetching municipalities:', error);
    }
  };
  
  const detectLocation = async () => {
    try {
      const response = await axios.get(`${API}/analytics/detect-location`);
      if (response.data.detected) {
        setDetectedLocation({
          city: response.data.city,
          region: response.data.region,
          country: response.data.country
        });
      }
    } catch (error) {
      console.error('Error detecting location:', error);
    }
  };

  // Start the test after pre-test questions
  const handleStartTest = async () => {
    // Create analytics session with demographic and geolocation data
    try {
      await axios.post(`${API}/analytics/session`, {
        session_id: sessionId,
        language,
        pre_test_answer: preTestAnswer,
        municipality: municipality || null,
        gender: gender || null,
        age_group: ageGroup || null,
        // Auto-detected location
        detected_city: detectedLocation?.city || null,
        detected_region: detectedLocation?.region || null,
        detected_country: detectedLocation?.country || null
      });
    } catch (error) {
      console.error('Error creating analytics session:', error);
    }
    setShowPreTest(false);
  };

  // Filter municipalities for autocomplete
  const filteredMunicipalities = municipality.length > 1
    ? municipalities.filter(m => 
        m.toLowerCase().includes(municipality.toLowerCase())
      ).slice(0, 5)
    : [];

  const getAllQuestions = () => {
    if (!questions) return [];
    const categories = ['passion', 'talent', 'mission', 'profession'];
    return categories.flatMap(cat => {
      const catQuestions = questions[cat];
      if (!catQuestions || !Array.isArray(catQuestions)) return [];
      return catQuestions.map(q => ({ ...q, category: cat }));
    });
  };

  const allQuestions = getAllQuestions();
  const currentQuestion = allQuestions[currentQuestionIndex];
  const totalQuestions = allQuestions.length;
  const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;

  // Early return if no questions loaded yet or current question is undefined
  if (!currentQuestion || totalQuestions === 0) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#A4187A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1D1D1D]/60">{language === 'fr' ? 'Chargement...' : 'Laden...'}</p>
        </div>
      </div>
    );
  }

  const getCurrentCategory = () => {
    if (currentQuestionIndex < 5) return 'passion';
    if (currentQuestionIndex < 10) return 'talent';
    if (currentQuestionIndex < 15) return 'mission';
    return 'profession';
  };

  // Handle single answer selection (radio button style)
  const handleAnswerSelect = (answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answerIndex
    }));
    
    // Auto-advance to next question after a short delay (better UX)
    if (currentQuestionIndex < totalQuestions - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 400); // 400ms delay for visual feedback
    }
  };

  // Select neutral option (-1 = neutral, no points)
  const handleNeutralSelect = () => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: -1 // -1 indicates neutral/no answer
    }));
    
    // Auto-advance for neutral selection too
    if (currentQuestionIndex < totalQuestions - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 400);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Format answers - filter out neutral answers (-1)
      const formattedAnswers = Object.entries(answers)
        .filter(([_, answerIndex]) => answerIndex !== -1)
        .map(([questionId, answerIndex]) => ({
          question_id: parseInt(questionId),
          answer_index: answerIndex
        }));

      // Count neutral answers for analytics
      const neutralCount = Object.values(answers).filter(a => a === -1).length;

      const response = await axios.post(`${API}/submit`, {
        answers: formattedAnswers,
        language
      });

      // Update analytics session with Stage 1 completion
      try {
        const topDomains = Object.entries(response.data.scores || {})
          .sort(([,a], [,b]) => b - a)
          .slice(0, 3)
          .map(([domain]) => domain);
        
        await axios.patch(`${API}/analytics/session/${sessionId}`, {
          stage1_completed: true,
          suggested_careers: response.data.careers?.map(c => c.name) || [],
          top_domains: topDomains,
          domain_scores: response.data.scores || {},  // Save full scores for complete profile
          neutral_answers_count: neutralCount
        });
      } catch (err) {
        console.error('Error updating analytics:', err);
      }

      // Save complete user results to database
      try {
        // Build complete answers with text for reference
        const completeAnswers = Object.entries(answers).map(([questionId, answerIndex]) => ({
          question_id: parseInt(questionId),
          answer_index: answerIndex,
          is_neutral: answerIndex === -1
        }));

        // Format careers for storage
        const suggestedCareers = response.data.careers?.map(c => ({
          name: c.name,
          match: c.match,
          domains: c.domains,
          description: c.description
        })) || [];

        await axios.post(`${API}/user-results`, {
          session_id: sessionId,
          language,
          // Demographics
          age_group: ageGroup || null,
          gender: gender || null,
          municipality: municipality || null,
          region: detectedLocation?.region || null,
          // Pre-test
          pre_test_answer: preTestAnswer,
          // Stage 1
          stage1_answers: completeAnswers,
          domain_scores: response.data.scores || {},
          suggested_careers: suggestedCareers
        });
        console.log('User results saved successfully');
      } catch (err) {
        console.error('Error saving user results:', err);
        // Don't block navigation if save fails
      }

      // Pass sessionId to results for Stage 2 tracking
      navigate('/results', { state: { result: response.data, sessionId, preTestAnswer } });
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setSubmitting(false);
    }
  };

  const isCurrentAnswered = answers[currentQuestion?.id] !== undefined;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const currentAnswer = answers[currentQuestion?.id];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A4187A]"></div>
      </div>
    );
  }

  // Pre-test screen
  if (showPreTest) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
        <Header />
        <main className="flex-1 px-4 md:px-8 py-8">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg border border-[#E0E0E0] overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#A4187A] to-[#8E24AA] p-6 text-white text-center">
                <div className="w-16 h-16 bg-white/20 rounded-2xl mx-auto flex items-center justify-center mb-4">
                  <Lightbulb className="w-8 h-8" />
                </div>
                <h1 className="text-2xl font-bold">{msg.preTestTitle}</h1>
                <p className="text-white/80 mt-1">{msg.preTestSubtitle}</p>
              </div>

              <div className="p-6 space-y-6">
                {/* Pre-test question */}
                <div>
                  <h2 className="text-lg font-semibold text-[#1D1D1D] mb-4">
                    {preTest.question}
                  </h2>
                  <div className="space-y-3">
                    {preTest.answers.map((answer) => (
                      <button
                        key={answer.value}
                        onClick={() => setPreTestAnswer(answer.value)}
                        className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                          preTestAnswer === answer.value
                            ? 'border-[#A4187A] bg-[#F3E5F5]'
                            : 'border-[#E0E0E0] hover:border-[#A4187A]/50 hover:bg-[#EBEBEB]'
                        }`}
                        data-testid={`pretest-answer-${answer.value}`}
                      >
                        <span className="text-2xl">{answer.icon}</span>
                        <span className={`font-medium ${
                          preTestAnswer === answer.value ? 'text-[#A4187A]' : 'text-[#1D1D1D]'
                        }`}>
                          {answer.text}
                        </span>
                        {preTestAnswer === answer.value && (
                          <Check className="w-5 h-5 text-[#A4187A] ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Privacy notice - between first question and demographic questions */}
                <div className="bg-[#F3E5F5]/50 rounded-xl p-4">
                  <p className="text-sm text-[#1D1D1D]/70 flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 text-[#A4187A] flex-shrink-0" />
                    {msg.demographicNotice}
                  </p>
                </div>

                {/* Gender question (REQUIRED) */}
                <div>
                  <label className="block text-sm font-medium text-[#1D1D1D] mb-2">
                    {msg.genderLabel} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-colors bg-white ${
                      gender ? 'border-[#A4187A] bg-[#F3E5F5]/30' : 'border-[#E0E0E0] focus:border-[#A4187A]'
                    }`}
                    data-testid="gender-select"
                  >
                    {demoOptions.genderRequired.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.text}</option>
                    ))}
                  </select>
                </div>

                {/* Age Group (REQUIRED) */}
                <div>
                  <label className="block text-sm font-medium text-[#1D1D1D] mb-2">
                    {msg.ageGroupLabel} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={ageGroup}
                    onChange={(e) => setAgeGroup(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-colors bg-white ${
                      ageGroup ? 'border-[#A4187A] bg-[#F3E5F5]/30' : 'border-[#E0E0E0] focus:border-[#A4187A]'
                    }`}
                    data-testid="age-group-select"
                  >
                    {demoOptions.ageGroupRequired.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.text}</option>
                    ))}
                  </select>
                </div>

                {/* Municipality (REQUIRED) */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-[#FB6823]" />
                    <label className="text-sm font-medium text-[#1D1D1D]">
                      {msg.municipalityLabel}
                    </label>
                    <span className="text-red-500">*</span>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={municipality}
                      onChange={(e) => {
                        setMunicipality(e.target.value);
                        setShowMunicipalitySuggestions(true);
                      }}
                      onFocus={() => setShowMunicipalitySuggestions(true)}
                      onBlur={() => setTimeout(() => setShowMunicipalitySuggestions(false), 200)}
                      placeholder={msg.municipalityPlaceholder}
                      className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-colors ${
                        municipality ? 'border-[#A4187A] bg-[#F3E5F5]/30' : 'border-[#E0E0E0] focus:border-[#FB6823]'
                      }`}
                      data-testid="municipality-input"
                    />
                    {/* Autocomplete suggestions */}
                    {showMunicipalitySuggestions && filteredMunicipalities.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-[#E0E0E0] rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                        {filteredMunicipalities.map((m) => (
                          <button
                            key={m}
                            onClick={() => {
                              setMunicipality(m);
                              setShowMunicipalitySuggestions(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-[#F3E5F5] text-[#1D1D1D] transition-colors"
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Start button */}
                <button
                  onClick={handleStartTest}
                  disabled={!preTestAnswer || !gender || !ageGroup || !municipality}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                    preTestAnswer && gender && ageGroup && municipality
                      ? 'bg-gradient-to-r from-[#A4187A] to-[#8E24AA] text-white hover:shadow-lg hover:scale-[1.02]'
                      : 'bg-[#E0E0E0] text-[#1D1D1D]/40 cursor-not-allowed'
                  }`}
                  data-testid="start-test-btn"
                >
                  {msg.startTest}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  const categoryColor = categoryColors[getCurrentCategory()];
  const CategoryIcon = categoryIcons[getCurrentCategory()];

  // Calculate global progress (questionnaire is step 1, which is 50% of the journey)
  const globalProgress = Math.round((progress / 100) * 50); // Step 1 = 0-50%

  return (
    <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
      <Header />
      
      <main className="flex-1 px-4 md:px-8 py-6">
        <div className="max-w-3xl mx-auto">
          
          {/* Global Step Indicator */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            {/* Step Title */}
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="flex items-center gap-1 text-sm font-semibold text-[#A4187A]">
                <span className="w-6 h-6 rounded-full bg-[#A4187A] text-white flex items-center justify-center text-xs">1</span>
                <span>{t('stepIndicator.step1Title')}</span>
              </div>
            </div>
            
            {/* Global Progress Bar */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-2 bg-[#E0E0E0] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#A4187A] to-[#FB6823] transition-all duration-300"
                  style={{ width: `${globalProgress}%` }}
                />
              </div>
              <span className="text-xs font-medium text-[#A4187A]">{globalProgress}%</span>
            </div>
            
            {/* Step Indicators Visual */}
            <div className="flex items-center justify-center gap-4 text-xs text-[#1D1D1D]/60">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-[#A4187A]"></div>
                <span>{language === 'fr' ? 'Étape 1' : 'Stap 1'}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-[#E0E0E0]" />
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-[#E0E0E0]"></div>
                <span>{language === 'fr' ? 'Étape 2' : 'Stap 2'}</span>
              </div>
            </div>
          </motion.div>
          
          {/* Progress Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#1D1D1D]/60">
                {t('question')} {currentQuestionIndex + 1} {t('of')} {totalQuestions}
              </span>
              <div className="flex items-center gap-2">
                <div 
                  className="category-icon w-10 h-10"
                  style={{ backgroundColor: categoryColor }}
                >
                  <CategoryIcon className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-[#1D1D1D]">
                  {t('category')}: {t(getCurrentCategory())}
                </span>
              </div>
            </div>
            <div className="progress-container" data-testid="progress-bar">
              <div 
                className="progress-bar"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Instruction Message - Purple banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#A4187A]/10 border border-[#A4187A]/20 rounded-xl p-4 mb-4"
          >
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-[#A4187A] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#A4187A] font-medium">
                {msg.instruction}
              </p>
            </div>
          </motion.div>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestionIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="neo-card mb-6"
              data-testid="question-card"
            >
              <h2 className="text-xl md:text-2xl font-bold text-[#1D1D1D] mb-6">
                {currentQuestion?.question}
              </h2>

              {/* Answer Options - Radio Button Style */}
              <div className="space-y-3">
                {currentQuestion?.answers.map((answer, index) => {
                  const isSelected = currentAnswer === index;
                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      type="button"
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 select-none cursor-pointer ${
                        isSelected
                          ? 'border-[#A4187A] bg-[#A4187A] text-white shadow-lg'
                          : 'border-[#E0E0E0] bg-white md:hover:border-[#A4187A]/50 md:hover:bg-[#F3E5F5]/30 active:border-[#A4187A]/50 active:bg-[#F3E5F5]/30'
                      }`}
                      style={{ 
                        WebkitTapHighlightColor: 'transparent',
                        touchAction: 'manipulation',
                        WebkitUserSelect: 'none',
                        userSelect: 'none'
                      }}
                      data-testid={`answer-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Radio Button Circle */}
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected 
                            ? 'border-white bg-white' 
                            : 'border-[#A4187A]/40'
                        }`}>
                          {isSelected && (
                            <div className="w-3 h-3 rounded-full bg-[#A4187A]" />
                          )}
                        </div>
                        <span className={`font-medium ${isSelected ? 'text-white' : 'text-[#1D1D1D]'}`}>
                          {answer.text}
                        </span>
                      </div>
                    </button>
                  );
                })}

                {/* Neutral Option - "Je ne sais pas encore" */}
                <div className="pt-4 mt-4 border-t border-[#E0E0E0]">
                  <button
                    onClick={handleNeutralSelect}
                    type="button"
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 select-none cursor-pointer ${
                      currentAnswer === -1
                        ? 'border-[#FB6823] bg-[#FB6823]/10 text-[#FB6823]'
                        : 'border-dashed border-[#E0E0E0] bg-white md:hover:border-[#FB6823]/50 md:hover:bg-[#FB6823]/5 active:border-[#FB6823]/50 active:bg-[#FB6823]/5'
                    }`}
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation',
                      WebkitUserSelect: 'none',
                      userSelect: 'none'
                    }}
                    data-testid="answer-neutral"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        currentAnswer === -1
                          ? 'border-[#FB6823] bg-[#FB6823]' 
                          : 'border-[#FB6823]/40'
                      }`}>
                        {currentAnswer === -1 && (
                          <HelpCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className={`font-medium ${currentAnswer === -1 ? 'text-[#FB6823]' : 'text-[#1D1D1D]/60'}`}>
                        {msg.neutralOption}
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Supportive Guidance Message */}
              <div className="mt-6 pt-4 border-t border-[#E0E0E0]">
                <p className="text-xs text-[#1D1D1D]/50 italic flex items-start gap-2">
                  <HelpCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {msg.guidance}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className={`neo-button-outline flex items-center gap-2 ${
                currentQuestionIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-testid="prev-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('previous')}
            </button>

            {isLastQuestion ? (
              <button
                onClick={handleSubmit}
                disabled={!isCurrentAnswered || submitting}
                className={`neo-button-secondary flex items-center gap-2 ${
                  !isCurrentAnswered || submitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="submit-btn"
              >
                {submitting ? t('loading') : t('seeResults')}
                <Check className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!isCurrentAnswered}
                className={`neo-button-primary flex items-center gap-2 ${
                  !isCurrentAnswered ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="next-btn"
              >
                {t('next')}
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
